import { Box, Flex, Select } from "@mantine/core";
import SRD_DataTable from "components/DataTable";
import { getMapData } from "pages/Admin/Device/apis/adminDeviceApis";
import { getGroupList } from "pages/Admin/Group/apis/adminGroupApis";
import { userState } from "pages/Login/atom/userState";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import defaultMarker from "./marker-default.png";
import redMarker from "./marker-red.png";
import yellowMarker from "./marker-yellow-22x33.png";
import blackMarker from "./marker-black-22x33.png";

const adminOption = {value : null, label : "전체 그룹"};

// SearchBar 컴포넌트 (메모이제이션으로 불필요한 리렌더링 방지)
const SearchBar = React.memo(({ adminLevel, groupList, group, onGroupChange }) => {
  return (
    <Flex gap={15} mb={10} align="center" style={{ width: "100%" }}>
      <Select
        disabled={!adminLevel}
        data={groupList ?? []}
        value={group}
        placeholder="그룹선택"
        onChange={onGroupChange}
        style={{
          flex: 1,
        }}
      />
    </Flex>
  );
});

function HomeMap() {
  const mapElement = useRef(null);
  const mapInstance = useRef(null);  // 지도 인스턴스 저장
  const markersRef = useRef([]);     // 마커들 저장
  const prevGroupRef = useRef(undefined); // 이전 그룹 저장 (undefined = 초기 상태)
  const navigate = useNavigate();
  const [user] = useRecoilState(userState);
  const level = Number(user?.fn_level);
  const adminLevel = [0].includes(level);
  const loginGroupId = adminLevel ? null : user?.fn_group_id;

  const [group, setGroup] = useState(loginGroupId);
  const [page, setPage] = useState(1);

  const [data, setData] = useState();

  const { data: groupListData } = useQuery(
    "groupList",
    () =>
      getGroupList(1000, 1).then((res) =>
        res.map((item) => ({ value: item.group_id, label: item.group_nm }))
      ),
    {
      staleTime: 5 * 60 * 1000, // 5분
    }
  );

  // 그룹 리스트 메모이제이션 (전체 그룹 옵션 추가)
  const groupList = useMemo(() => {
    if (!adminLevel || !groupListData || groupListData.length < 1) {
      return groupListData ?? [];
    }
    const grList = groupListData.map((record) => record.label);
    if (!grList.includes("전체 그룹")) {
      return [adminOption, ...groupListData];
    }
    return groupListData;
  }, [adminLevel, groupListData]);

  useEffect(() => {
      setPage(1);
    }, [group]);

  // 데이터 fetch 함수
  const fetchMapData = useCallback(() => {
    getMapData(1000, 1, group)
      .then(res => setData(res.deviceList));
  }, [group]);

  // 초기 데이터 로드 및 주기적 갱신
  useEffect(() => {
    fetchMapData();
    const interval = setInterval(fetchMapData, 10000); // 10초마다 갱신
    return () => clearInterval(interval);
  }, [fetchMapData]);

  // const onClick = () => {
  //   // alert(`${(group, site, range.start, range.end)}`);
  // };

  // 전체 그룹 선택 시 페이지 리로드
  useEffect(() => {
    // 특정 그룹에서 전체 그룹으로 변경될 때만 리로드
    if (prevGroupRef.current !== undefined && prevGroupRef.current !== null && group === null) {
      window.location.reload();
    }
  }, [group]);

  // 지도 초기화 및 그룹 변경 시 재조정
  useEffect(() => {
    const { naver } = window;
    if (!mapElement.current || !naver || !data || data.length < 1) return;
    
    var loc;
    if (!adminLevel) {
      loc = data.filter(item => item.group_id === (group ?? user?.fn_group_id));
    } else {
      // admin 사용자: 그룹 선택 시 해당 그룹만, 전체 그룹 선택 시 전체
      if (group) {
        loc = data.filter(item => item.group_id === group);
      } else {
        loc = data;
      }
    }
    
    if (loc.length < 1) return;
    
    // 그룹이 변경되지 않았으면 지도 재생성 하지 않음
    const groupChanged = prevGroupRef.current !== group;
    if (mapInstance.current && !groupChanged) return;
    
    let sumMap_xcord = 0.0;
    let sumMap_ycord = 0.0;
    let minMap_xcord = 999.0;
    let minMap_ycord= 999.0;
    let maxMap_xcord = 0.0;
    let maxMap_ycord = 0.0;

    loc.forEach((item) =>{
      sumMap_xcord = sumMap_xcord + Number(item.map_xcord);
      sumMap_ycord = sumMap_ycord + Number(item.map_ycord);
      if(minMap_xcord > Number(item.map_xcord)) minMap_xcord = Number(item.map_xcord);
      if(minMap_ycord > Number(item.map_ycord)) minMap_ycord = Number(item.map_ycord);
      if(maxMap_xcord < Number(item.map_xcord)) maxMap_xcord = Number(item.map_xcord);
      if(maxMap_ycord < Number(item.map_ycord)) maxMap_ycord = Number(item.map_ycord);
    });
    const avgMap_xcord = (sumMap_xcord / loc.length).toFixed(7);
    const avgMap_ycord = (sumMap_ycord / loc.length).toFixed(7);
    const Latitudemargin = 0.00005;
    const Longitudemargin = 0.002;
    
    const center = new naver.maps.LatLng(avgMap_ycord,avgMap_xcord);
    const sw = new naver.maps.LatLng(minMap_ycord-Latitudemargin,minMap_xcord-Longitudemargin);
    const ne = new naver.maps.LatLng(maxMap_ycord+Latitudemargin,maxMap_xcord+Longitudemargin);
    const location = new naver.maps.LatLngBounds(sw ,ne);
    const mapOptions = {
      center: center,
      bounds: location,
      zoomControl: true,
      zoomControlOptions: {
        position: naver.maps.Position.TOP_RIGHT,
      },
    };
  
    const mapElementStyle = {
      width: '100%',
      height: "calc(100vh - 180px)",
    };
  
    // 기존 지도가 있으면 제거
    if (mapInstance.current) {
      mapInstance.current.destroy();
    }
    
    // 지도 생성 (그룹 변경 시 또는 첫 로딩 시)
    mapInstance.current = new naver.maps.Map(mapElement.current, mapOptions);
    mapElement.current.style.width = mapElementStyle.width;
    mapElement.current.style.height = mapElementStyle.height;

    requestAnimationFrame(() => {
      mapInstance.current?.autoResize();
    });
    
    // 지도 생성 완료 후 현재 그룹 저장
    prevGroupRef.current = group;
  }, [group, adminLevel, user?.fn_group_id, data]);

  // 마커 업데이트 (데이터가 변경될 때마다)
  useEffect(() => {
    const { naver } = window;
    if (!mapInstance.current || !naver || !data || data.length < 1) return;
    
    // 기존 마커 모두 제거
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    
    // 새 마커 추가 (원본과 동일하게 전체 data로 생성)
    if (data.length > 0) {
      data.forEach((item, idx) => {
        if (item.map_ycord && item.map_xcord) {
          // 마커 아이콘 선택
          let markerIcon = defaultMarker;
          if (item.vido_url === "COMM_SPA") {
            markerIcon = yellowMarker;  // 분사 중
          } else if (item.vido_url === "COMM_ERR") {
            markerIcon = blackMarker;   // 통신 에러
          } else if (item.vido_url === "COMM_WRR") {
            markerIcon = redMarker;     // 장치 고장(예비)
          }
          
          const marker = new naver.maps.Marker({
            position: new naver.maps.LatLng(item.map_ycord, item.map_xcord),
            map: mapInstance.current,
            icon: markerIcon,
            title: "[ " + item.bsd_nm + " ]\n@" + item.field_nm,
            cursor: "pointer",
          });

          // 클릭 이벤트 핸들러 등록
          naver.maps.Event.addListener(marker, "click", () => {
            navigate(`/home`,{
              state: {
                linkGroupId : item.group_id,
                linkFieldId : item.field_id
              }
            });
          });
          
          markersRef.current.push(marker);
        }
      });
    }
  }, [data, navigate]);

  const columns = [
    {
      accessor: "index",
      title: "번호",
      textAlignment: "center",
      width: 30,
      // render: (record) => (data.indexOf(record) + 1)+PAGE_SIZE*(page-1),
      render: (record) => data.indexOf(record) + 1,
    },
    {
      accessor: "bsd_nm",
      title: "분사 장치",
      width: 200,
    },
    {
      accessor: "field_nm",
      title: "관제 현장",
      width: 200,
      render: ({ field_nm }) => (
        <Box c={field_nm !== "--------" ? "blue" : "black"}>
          {field_nm}
        </Box>
    ),
      onClick: function (e) {
        if(e.record.field_nm === "--------") return;
        navigate(`/home`,{
          state: {
            // linkGroupId : group,
            linkGroupId : e.record.group_id,
            linkFieldId : e.record.field_id
          }
        });
      }
    },
  ];
  // <Button onClick={() => onClick()}>검색</Button>
  const handleGroupChange = useCallback((value) => setGroup(value), []);
  
  return (
    <Flex gap={15} align="stretch">
      <div
        ref={mapElement}
        style={{
          width: "900px",
          height: "calc(100vh - 120px)", // 지도 컨테이너의 높이를 테이블 창과 동일하게
          borderRadius: "0.25rem",
          border: "1px solid #dee2e6",
        }}
      />
      <div style={{ width: "calc(100% - 900px)", height: "calc(100vh - 120px)" }}> {/* 테이블 컨테이너의 높이를 지도 창과 동일하게 */}
        <SRD_DataTable
          searchBar={<SearchBar adminLevel={adminLevel} groupList={groupList} group={group} onGroupChange={handleGroupChange} />}
          data={data}
          columns={columns}
          page={page}
          setPage={setPage}
          idAccessor={(record) =>
            [
              record.group_id,
              record.field_id,
              record.object_id,
              record.bsd_id,
            ]
              .map(v => v ?? "")
              .join("-")
          }
        />
      </div>
    </Flex>
  );
}

export default HomeMap;
